import type { FC } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import styled from "styled-components";
import type { IHelpsiteArticle } from "@types-contentful";
import { colours, device } from "@helpers";
import { helpsiteTrackLink } from "@helpers/helpsite-mixpanel";
import Icon from "@components/Icon";

const BREAKPOINT_1024 = "1024px";

const RelatedSection = styled.section`
	margin-top: 32px;

	@media (min-width: ${BREAKPOINT_1024}) {
		margin-top: 48px;
	}
`;

/* Figma: Euclid Circular A Trial, Medium 500, 24px, line-height 100% */
const RelatedHeader = styled.h2`
	font-family: var(--euclid-medium);
	font-size: 24px;
	font-weight: 500;
	line-height: 100%;
	letter-spacing: 0%;
	margin-bottom: 24px;
	color: ${colours.MIDNIGHT_BLUE};
`;

/* Same card style as HelpArticleList ArticleCard */
const RelatedList = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: 20px;
	width: 100%;
	box-sizing: border-box;

	@media (min-width: ${device.laptop}) {
		gap: 30px;
	}
`;

const RelatedCard = styled(Link)`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	gap: 11px;
	padding: 15px 10px;
	background-color: rgba(255, 255, 255, 0.5);
	border: 1px solid rgba(0, 0, 0, 0.15);
	border-radius: 10px;
	text-decoration: none;
	color: inherit;
	width: 100%;
	box-sizing: border-box;
	transition: none !important;

	&:hover,
	&:active,
	&:focus {
		opacity: 1 !important;
		background-color: inherit !important;
		transform: none !important;
		box-shadow: 0 4px 12px rgba(18, 18, 78, 0.08);
		border-color: rgba(18, 18, 78, 0.25);
	}

	@media (min-width: ${device.laptop}) {
		padding: 20px;
		background-color: ${colours.WHITE};
		justify-content: flex-start;
	}
`;

/* Same as HelpArticleList IconContainer: 50x50, #f0f0f0, border-radius 6px */
const IconContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 50px;
	height: 50px;
	background-color: #f0f0f0;
	border-radius: 6px;
	flex-shrink: 0;
`;

const RelatedContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	flex: 1;
	min-width: 0;
	text-align: left;
`;

const RelatedTitle = styled.h3`
	font-family: var(--euclid-medium);
	font-size: 18px;
	font-weight: 500;
	line-height: 1.2679999669392903em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0;
	text-align: left;

	@media (min-width: ${device.laptop}) {
		font-size: 20px;
		line-height: 1.268000030517578em;
	}
`;

interface HelpRelatedArticlesProps {
	relatedArticles: IHelpsiteArticle[];
	/** Article entry id -> parent topic slug segment */
	articleTopicSlugById?: Record<string, string>;
	/** Current page topic slug fallback */
	currentTopicSlug?: string;
}

function stripLeadingSlash(segment: string): string {
	return segment.startsWith("/") ? segment.slice(1) : segment;
}

const HelpRelatedArticles: FC<HelpRelatedArticlesProps> = ({
	relatedArticles,
	articleTopicSlugById = {},
	currentTopicSlug,
}) => {
	const router = useRouter();
	const pagePath = router.asPath.split("?")[0];

	return (
		<RelatedSection>
			<RelatedHeader>Related Articles</RelatedHeader>
			<RelatedList>
				{relatedArticles.map((article) => {
					const articleSlug = stripLeadingSlash(
						article.fields.articleRelativeUrl || "",
					);
					const topicSlug =
						articleTopicSlugById[article.sys.id] ?? currentTopicSlug;
					const articleUrl = topicSlug
						? `/help/${topicSlug}/${articleSlug}`
						: `/help/${articleSlug}`;

					return (
						<RelatedCard
							key={article.sys.id}
							href={articleUrl}
							onClick={() =>
								helpsiteTrackLink({
									helpsite_section: "related_articles",
									page_path: pagePath,
									link_href: articleUrl,
									label: article.fields.articleMainHeader,
								})
							}
						>
							<IconContainer>
								<Icon
									iconName={article.fields.iconRef || "text"}
									iconSizes={{
										iconMobile: 40,
										iconTablet: 40,
										iconLaptop: 40,
										iconDesktop: 40,
										iconDesktopLarge: 40,
									}}
									alt={article.fields.articleMainHeader}
								/>
							</IconContainer>
							<RelatedContentWrapper>
								<RelatedTitle>{article.fields.articleMainHeader}</RelatedTitle>
							</RelatedContentWrapper>
						</RelatedCard>
					);
				})}
			</RelatedList>
		</RelatedSection>
	);
};

export default HelpRelatedArticles;
